import { assertInterface, getLogger, tracker } from 'app-components/utils/core';
import { immutableSerialize } from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';

import { getCacheMetadata } from './cacheMetadata';
import { clearCachedState } from './clearCachedState';
import { type CacheObject, type Config, defaultGetCacheKey, type ReducerConfig } from './config';
import { decryptString } from './encryption';

const log = getLogger('getCachedState.ts');

type RestoredState = { [key: string]: any };

const getConfigForKey = (key: string, config: Config) => config.persistableStateConfig[key];

const getKeyForIndex = (i: number, config: Config) => config.stateKeys[i];

const getConfigForIndex = (i: number, config: Config) => getConfigForKey(getKeyForIndex(i, config), config);

export const defaultCacheToState = <D>(data: D) => {
  let state = immutableSerialize.convertToImmutableRecursive(data);
  // reset isLoading
  if (state.isLoading && state.set) {
    state = state.set('isLoading', false);
  }

  // remove optimistic/ghost records
  if (state.resourcesById && state.resourcesById.set && state.resourcesById.filter) {
    state = state.set(
      'resourcesById',
      state.resourcesById.filter((resource: { id: string }, key: string) => resource.id === key),
    );
  }

  return state;
};

function transformCacheToState(parsedCache: CacheObject, config: ReducerConfig<keyof RootState>, name: string) {
  const { version, data } = parsedCache;

  if (version !== config.version) {
    log.warn(`${name} version mismatch - reset to defaults (${version} -> ${config.version})`);

    return undefined;
  }

  const { cacheToState = defaultCacheToState } = config;
  return cacheToState(data);
}

async function migrateCache(persistConfig: Config) {
  const migrations = persistConfig.stateKeys.map((key) => {
    const config = getConfigForKey(key, persistConfig);

    return config.migrate?.();
  });

  const migratedCaches = await Promise.all(migrations);

  return migratedCaches.reduce((state, cache, index) => {
    const config = getConfigForIndex(index, persistConfig);

    const name = getKeyForIndex(index, persistConfig);

    if (cache) {
      state[name] = transformCacheToState(cache, config, name);
    }

    return state;
  }, {} as RestoredState);
}

async function decryptAndParseCache(caches: Array<string | null | undefined>, config: Config) {
  const getCacheKey = config.getCacheKeyForStateKey || defaultGetCacheKey;

  const state: RestoredState = {};
  const removals: Array<Promise<void>> = [];

  for (const [index, cached] of caches.entries()) {
    if (!cached) continue;

    const reducerName = getKeyForIndex(index, config);

    const reducerConfig = getConfigForIndex(index, config);

    const shouldDecrypt = Boolean(reducerConfig.encrypt);

    // A wrong key, a corrupt entry, or an entry written before `encrypt` was enabled on this slice
    // all land here. Depending on the byte pattern, decryptString either throws 'Malformed UTF-8
    // data' or returns '' and JSON.parse throws instead, so both steps have to be guarded.
    // 'transform' is reported separately because it means a cacheToState bug rather than bad data.
    let stage: 'decrypt' | 'parse' | 'transform' = 'decrypt';

    try {
      const cachedString = shouldDecrypt ? decryptString(cached, config.encryptionKey) : cached;

      stage = 'parse';
      const parsed: CacheObject = JSON.parse(cachedString);

      stage = 'transform';
      state[reducerName] = transformCacheToState(parsed, reducerConfig, reducerName);
      if (parsed.version !== reducerConfig.version) {
        // A reset cache entry has no migration path. Drop it so later launches
        // do not repeatedly read and parse a potentially large stale slice.
        removals.push(config.storageInterface.removeItem(getCacheKey(reducerName)));
      }
    } catch (error) {
      // Reset this slice only. Rethrowing would abort the whole rehydrate and reset every slice,
      // including auth, which has no refresh action to recover from.
      log.warn(`${reducerName} cache unreadable at ${stage} - reset to defaults (${error})`);

      // Distinct from persistLoadError, which means the whole rehydrate failed. This is one slice
      // resetting while the rest of the store loads normally, and it is otherwise silent.
      tracker.track(tracker.events.persistSliceResetOnLoad, {
        reducerName,
        stage,
        encrypted: shouldDecrypt,
        error: String(error),
      });

      // Bugsnag as well, so a reset shows up in error triage and not only in analytics.
      assertInterface.assert(false, `${reducerName} cache unreadable at ${stage}`, error);

      // Drop the unreadable entry so it cannot be re-read on the next launch.
      removals.push(config.storageInterface.removeItem(getCacheKey(reducerName)));
    }
  }

  // allSettled, not all: a storage backend that rejects on removal must not abort the rehydrate we
  // just recovered. The entry stays on disk and resets again next launch, which is survivable.
  await Promise.allSettled(removals);

  return state;
}

export async function getCachedState(config: Config) {
  log.info(`persist version: ${config.version}`);

  const metadata = await getCacheMetadata(config);

  if (metadata.version !== config.version) {
    log.warn(`global version mismatch - migrating cached data (${metadata.version} -> ${config.version})`);

    const migrated = await migrateCache(config);

    await clearCachedState(config);

    return migrated;
  }

  const getCacheKey = config.getCacheKeyForStateKey || defaultGetCacheKey;
  const tasks = config.stateKeys.map((key) => config.storageInterface.getItem(getCacheKey(key)));

  const cachedStates = await Promise.all(tasks);

  return decryptAndParseCache(cachedStates, config);
}
